import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
  Legend,
  Area,
  AreaChart,
} from "recharts";
import { formatINR } from "@/lib/api";

export const PALETTE = [
  "#1F3A5F", // deep slate
  "#E76F1A", // amber
  "#2BA88A", // teal
  "#B23A48", // brick
  "#D4A017", // ochre
  "#5B7B9A", // muted blue
  "#7A4E8A", // plum (used sparingly)
  "#3F8F4D", // forest
];

const Tip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="card-surface px-3 py-2 text-xs">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={p.dataKey || p.name || `tip-${i}`} className="flex items-center gap-2 tabular">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: p.color || p.fill }}
          />
          <span className="text-muted-foreground">{p.name}</span>
          <span className="ml-auto font-semibold">
            {formatter ? formatter(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// Stable references — extracted so they aren't re-created on every render
const AREA_MARGIN = { top: 10, right: 12, left: 0, bottom: 0 };
const BAR_MARGIN = { top: 10, right: 24, left: 8, bottom: 0 };
const LEGEND_STYLE = { fontSize: 12 };
const Y_TICK_STYLE = { fontSize: 12 };
const BAR_RADIUS = [0, 6, 6, 0];

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2025-04" -> "Apr'25"
const formatMonthTick = (key) => {
  if (!key) return "";
  const [y, m] = String(key).split("-");
  const mi = Number(m) - 1;
  if (Number.isNaN(mi) || mi < 0 || mi > 11) return key;
  return `${MONTH_SHORT[mi]}'${y.slice(-2)}`;
};

// Compact INR: ₹10L, ₹1Cr (no decimals on Y-axis ticks; full values still in tooltip)
const formatINRCompact = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Number(n);
  if (v === 0) return "₹0";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}₹${Math.round(v / 1e7)}Cr`;
  if (abs >= 1e5) return `${sign}₹${Math.round(v / 1e5)}L`;
  if (abs >= 1e3) return `${sign}₹${Math.round(v / 1e3)}K`;
  return `${sign}₹${Math.round(v)}`;
};

const useIsMobile = () => {
  const [m, setM] = React.useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  React.useEffect(() => {
    const onR = () => setM(window.innerWidth < 640);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  return m;
};

const PieLegendFormatter = (v) => (
  <span className="text-muted-foreground">{v}</span>
);

// Brand series colours used across charts (kept in sync with KPI tile tints)
const SERIES_COLOR = {
  bookings: "#1F3A5F",   // Brand navy/blue
  revenue: "#E76F1A",    // Brand orange
  collection: "#2BA88A", // Brand green
};

const TrendLegend = ({ items, fontSize }) => (
  <ul
    className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-1"
    style={{ fontSize, padding: 0, margin: 0, listStyle: "none" }}
  >
    {items.map((it) => (
      <li key={it.id} className="inline-flex items-center gap-1.5">
        <span
          className="inline-block w-2.5 h-0.5 rounded-full"
          style={{ backgroundColor: it.color, height: 3 }}
        />
        <span style={{ color: it.color, fontWeight: 500 }}>{it.label}</span>
      </li>
    ))}
  </ul>
);

export const RevenueTrendChart = ({ data }) => {
  const isMobile = useIsMobile();
  const tickInterval =
    isMobile && data.length > 8 ? Math.max(1, Math.ceil(data.length / 6) - 1) : 0;
  const tickStyle = { fontSize: isMobile ? 10 : 11, fill: "#64748b" };
  const legendItems = [
    { id: "bookings", label: "Bookings (TOV)", color: SERIES_COLOR.bookings },
    { id: "revenue", label: "Revenue (Recognised)", color: SERIES_COLOR.revenue },
    { id: "collection", label: "Collection", color: SERIES_COLOR.collection },
  ];
  return (
    <ResponsiveContainer width="100%" height={isMobile ? 240 : 290}>
      <AreaChart
        data={data}
        margin={{ top: 10, right: 8, left: isMobile ? -8 : -8, bottom: 0 }}
      >
        <defs>
          <linearGradient id="book" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES_COLOR.bookings} stopOpacity={0.28} />
            <stop offset="100%" stopColor={SERIES_COLOR.bookings} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES_COLOR.revenue} stopOpacity={0.32} />
            <stop offset="100%" stopColor={SERIES_COLOR.revenue} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="col" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES_COLOR.collection} stopOpacity={0.22} />
            <stop offset="100%" stopColor={SERIES_COLOR.collection} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 14% 92%)" />
        <XAxis
          dataKey="month"
          tickFormatter={formatMonthTick}
          tick={tickStyle}
          interval={tickInterval}
          tickMargin={6}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={formatINRCompact}
          width={isMobile ? 48 : 56}
          tick={tickStyle}
          tickCount={5}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          content={<Tip formatter={formatINR} />}
          labelFormatter={formatMonthTick}
        />
        <Legend
          verticalAlign="bottom"
          content={() => (
            <TrendLegend items={legendItems} fontSize={isMobile ? 10 : 12} />
          )}
        />
        <Area
          type="monotone"
          dataKey="bookings"
          name="Bookings (TOV)"
          stroke={SERIES_COLOR.bookings}
          strokeWidth={2}
          fill="url(#book)"
        />
        <Area
          type="monotone"
          dataKey="revenue"
          name="Revenue (Recognised)"
          stroke={SERIES_COLOR.revenue}
          strokeWidth={2}
          fill="url(#rev)"
        />
        <Area
          type="monotone"
          dataKey="collection"
          name="Collection"
          stroke={SERIES_COLOR.collection}
          strokeWidth={2}
          fill="url(#col)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Stacked Booking / Revenue / Collection bar — single horizontal bar per
// category with 3 ordered segments. Segments use the brand trend palette so
// the chart is visually consistent with the Revenue & Collection Trend.
//
//  Segment 1 (innermost): Collection           — darker (green)
//  Segment 2 (middle):    Revenue − Collection — lighter (orange)
//  Segment 3 (outer):     Booking  − Revenue   — darkest (navy)
//
// Edge-cases handled: if collection > revenue (advance), middle segment is 0.
// If revenue > booking (rare), outer segment is 0. Bar length always equals
// max(booking, revenue, collection).
const STACK_TIP_LABEL = {
  collection_seg: "Collection",
  revenue_gap: "Revenue (recognised)",
  booking_gap: "Booking (TOV)",
};

const StackedBRCTip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="card-surface px-3 py-2 text-xs">
      <div className="font-semibold mb-1">{label}</div>
      <div className="flex items-center gap-2 tabular">
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: SERIES_COLOR.collection }} />
        <span className="text-muted-foreground">Collection</span>
        <span className="ml-auto font-semibold">{formatINR(row.collection)}</span>
      </div>
      <div className="flex items-center gap-2 tabular">
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: SERIES_COLOR.revenue }} />
        <span className="text-muted-foreground">Revenue</span>
        <span className="ml-auto font-semibold">{formatINR(row.recognised_revenue)}</span>
      </div>
      <div className="flex items-center gap-2 tabular">
        <span className="inline-block w-2 h-2 rounded-full" style={{ background: SERIES_COLOR.bookings }} />
        <span className="text-muted-foreground">Booking (TOV)</span>
        <span className="ml-auto font-semibold">{formatINR(row.booking)}</span>
      </div>
    </div>
  );
};

export const StackedBRCChart = ({ data, height = 320 }) => {
  const isMobile = useIsMobile();
  // Pre-compute the 3 stack segments per row
  const computed = (data || []).map((d) => {
    const c = Number(d.collection || 0);
    const r = Number(d.recognised_revenue || 0);
    const b = Number(d.booking || d.revenue || 0);
    return {
      ...d,
      collection_seg: c,
      revenue_gap: Math.max(0, r - c),
      booking_gap: Math.max(0, b - Math.max(c, r)),
    };
  });
  // Sort tallest-first so the leaderboard reads naturally
  computed.sort(
    (a, b) =>
      Math.max(b.booking || 0, b.recognised_revenue || 0, b.collection || 0) -
      Math.max(a.booking || 0, a.recognised_revenue || 0, a.collection || 0)
  );

  const legendItems = [
    { id: "collection", label: "Collection", color: SERIES_COLOR.collection },
    { id: "revenue", label: "Revenue (recognised)", color: SERIES_COLOR.revenue },
    { id: "bookings", label: "Booking (TOV)", color: SERIES_COLOR.bookings },
  ];

  return (
    <div>
      <TrendLegend items={legendItems} fontSize={isMobile ? 10 : 11} />
      <ResponsiveContainer width="100%" height={isMobile ? Math.min(height, 280) : height}>
        <BarChart
          data={computed}
          layout="vertical"
          margin={{ top: 6, right: isMobile ? 12 : 24, left: 4, bottom: 0 }}
          barCategoryGap="18%"
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 14% 92%)" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={formatINRCompact}
            tick={{ fontSize: isMobile ? 10 : 11, fill: "#64748b" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={isMobile ? 110 : 140}
            tick={{ fontSize: isMobile ? 10 : 12, fill: "#1F3A5F" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<StackedBRCTip />} cursor={{ fill: "rgba(31, 58, 95, 0.04)" }} />
          {/* Grouped (side-by-side) so Collection / Revenue / Booking are
              independently visible per vertical — no segment hides another. */}
          <Bar
            dataKey="booking"
            fill={SERIES_COLOR.bookings}
            name={STACK_TIP_LABEL.booking_gap}
            radius={[0, 4, 4, 0]}
            isAnimationActive={false}
            barSize={isMobile ? 7 : 10}
          />
          <Bar
            dataKey="recognised_revenue"
            fill={SERIES_COLOR.revenue}
            name={STACK_TIP_LABEL.revenue_gap}
            radius={[0, 4, 4, 0]}
            isAnimationActive={false}
            barSize={isMobile ? 7 : 10}
          />
          <Bar
            dataKey="collection_seg"
            fill={SERIES_COLOR.collection}
            name={STACK_TIP_LABEL.collection_seg}
            radius={[0, 4, 4, 0]}
            isAnimationActive={false}
            barSize={isMobile ? 7 : 10}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const HBarChart = ({ data, dataKey = "revenue", labelKey = "name", height = 320 }) => {
  const isMobile = useIsMobile();
  return (
    <ResponsiveContainer width="100%" height={isMobile ? Math.min(height, 280) : height}>
      <BarChart data={data} layout="vertical" margin={{ top: 10, right: isMobile ? 12 : 24, left: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(220 14% 92%)" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={formatINRCompact}
          tick={{ fontSize: isMobile ? 10 : 11, fill: "#64748b" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey={labelKey}
          width={isMobile ? 110 : 140}
          tick={{ fontSize: isMobile ? 10 : 12, fill: "#1F3A5F" }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<Tip formatter={formatINR} />} />
        <Bar dataKey={dataKey} radius={BAR_RADIUS}>
          {data.map((d, i) => (
            <Cell key={d[labelKey] ?? d.id ?? `bar-${i}`} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const DonutChart = ({ data, valueKey = "revenue", labelKey = "name", height = 280, formatter = formatINR }) => (
  <ResponsiveContainer width="100%" height={height}>
    <PieChart>
      <Tooltip content={<Tip formatter={formatter} />} />
      <Pie
        data={data}
        innerRadius={60}
        outerRadius={95}
        paddingAngle={2}
        dataKey={valueKey}
        nameKey={labelKey}
      >
        {data.map((d, i) => (
          <Cell key={d[labelKey] ?? d.id ?? `pie-${i}`} fill={PALETTE[i % PALETTE.length]} />
        ))}
      </Pie>
      <Legend
        verticalAlign="bottom"
        wrapperStyle={LEGEND_STYLE}
        formatter={PieLegendFormatter}
      />
    </PieChart>
  </ResponsiveContainer>
);
